<!--
  You can give the header the option to display the title left or right by specifying the prop positionTextLeft
-->
<template>
  <header id="header-overview" class="grid grid-cols-12 md:container md:mx-auto" data-component="HeaderOverview">
    <div
      class="relative z-20 col-span-12 row-end-3 row-start-auto ml-4 md:col-span-10 md:ml-0 lg:col-span-8 xl:col-span-6"
      :class="{
        'col-end-13 md:col-end-13 lg:col-end-13 xl:col-end-13 2xl:col-end-13': !positionTextLeft,
        'col-start-1 md:col-start-1 lg:col-start-1 xl:col-start-1 2xl:col-start-1': positionTextLeft,
      }"
    >
      <div
        class="heading-text p-6 text-2xl font-black leading-none rounded-l-2xl uppercase sm:p-8 md:text-3xl lg:p-12 lg:text-4xl xl:p-16 xl:text-5xl xl:font-bold"
        :class="{
          'heading-text--right': !positionTextLeft,
          'md:rounded-l-none md:rounded-r-2xl heading-text--left': positionTextLeft,
          'bg-app-lime text-black': !darkTitle,
          'bg-black text-white heading-text--dark-title': darkTitle,
          'mb-8 lg:mb-12 xl:mb-16 2xl:mb-20': !subtitle && !introduction,
        }"
      >
        <p v-if="preTitle" class="block mb-1 text-base font-semibold normal-case sm:text-lg md:text-xl lg:text-2xl xl:text-3xl">{{ preTitle }}</p>
        <h1>{{ title }}</h1>
      </div>
    </div>
    <div
      class="relative z-10 col-span-12 col-start-1 row-end-5 row-start-3 -mt-10 md:col-span-10 md:-mt-8 md:mr-0 lg:col-span-8 lg:-mt-8 xl:col-span-6 xl:-mt-20"
      :class="{
        'md:col-start-2 lg:col-start-4 xl:col-start-5 mr-4 ': !positionTextLeft,
        'md:col-start-2 lg:col-start-2 xl:col-start-2 mr-8 sm:mr-10': positionTextLeft,
      }"
    >
      <div
        v-if="subtitle || introduction"
        class="pb-6 pt-16 px-6 bg-white rounded-r-2xl sm:pb-8 sm:pt-16 sm:px-12 md:px-8 md:rounded-xl lg:pb-12 lg:pt-20 lg:px-12 xl:pb-16 xl:pt-32 xl:px-16"
      >
        <h2 v-if="subtitle" class="mb-2 text-lg font-bold leading-none xl:text-2xl text-black">{{ subtitle }}</h2>
        <div v-if="introduction" class="prose prose--app max-w-none font-serif text-base font-bold leading-snug xl:text-xl" v-html="introduction" />
        <slot name="headerButtons" />
      </div>
    </div>
    <figure
      v-if="image"
      class="margin-figure width-figure 2xl:col-span-11 col-span-12 2xl:col-end-12 col-start-1 row-end-4 row-start-1 2xl:rounded-b-2xl overflow-hidden xl:rounded-r-2xl"
    >
      <MoleculePicture class="block w-full h-full border-b-4 border-solid border-app-lime object-cover" :class="{ 'short-header-overview-image': shortImage }" :image="image" />
    </figure>
  </header>
</template>

<script setup lang="ts">
interface Props {
  preTitle?: string
  title?: string
  subtitle?: string
  introduction?: string
  image?: object
  positionTextLeft?: boolean
  darkTitle?: boolean
  shortImage?: boolean
}

defineProps<Props>()
</script>

<style lang="scss" scoped>
$width-before-md: calc(((100vw - theme('screens.md')) / 2));
$width-before-lg: calc(((100vw - theme('screens.lg')) / 2));
$width-before-xl: calc(((100vw - theme('screens.xl')) / 2));
$width-before-2xl: calc(((100vw - theme('screens.2xl')) / 2));

.margin-figure {
  @screen md {
    margin-left: calc(-1 * #{$width-before-md});
  }

  @screen lg {
    margin-left: calc(-1 * #{$width-before-lg});
  }

  @screen xl {
    margin-left: calc(-1 * #{$width-before-xl});
  }

  @screen 2xl {
    margin-left: auto;
  }
}

.width-figure {
  @apply w-screen;

  @screen xl {
    width: calc(theme('screens.xl') + #{$width-before-xl});
  }

  @screen 2xl {
    @apply w-full;
  }
}

.heading-text {
  @apply relative;

  &::before {
    @apply block bg-app-lime absolute top-0 h-full;
    z-index: -1;

    @screen md {
      content: '';
      width: $width-before-md;
    }

    @screen lg {
      width: $width-before-lg;
    }

    @screen xl {
      width: $width-before-xl;
    }
  }

  &.heading-text--dark-title::before {
    @apply bg-black;
  }
}

.heading-text--right {
  &::before {
    @screen md {
      right: calc(-1 * #{$width-before-md});
    }

    @screen lg {
      right: calc(-1 * #{$width-before-lg});
    }

    @screen xl {
      right: calc(-1 * #{$width-before-xl});
    }

    @screen 2xl {
      right: calc(-1 * #{$width-before-2xl});
    }
  }

  @media screen and (min-width: calc(1280px + 96px)) {
    @apply pr-0;
  }

  @screen 2xl {
    @apply pr-16;
  }

  @media screen and (min-width: calc(1750px + 96px)) {
    @apply pr-0;
  }
}

.heading-text--left {
  &::before {
    @screen md {
      left: calc(-1 * #{$width-before-md});
    }

    @screen lg {
      left: calc(-1 * #{$width-before-lg});
    }

    @screen xl {
      left: calc(-1 * #{$width-before-xl});
    }

    @screen 2xl {
      left: calc(-1 * #{$width-before-2xl});
    }
  }

  @media screen and (min-width: calc(1280px + 96px)) {
    @apply pl-0;
  }

  @screen 2xl {
    @apply pl-16;
  }

  @media screen and (min-width: calc(1750px + 96px)) {
    @apply pl-0;
  }
}

.short-header-overview-image {
  @screen lg {
    max-height: 480px;
  }

  @screen xl {
    max-height: 540px;
  }

  @screen 2xl {
    max-height: 600px;
  }
}
</style>
